import { useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

import type { MonthOption, ParentAttendanceCalendarDay } from '../../types/attendance';

export interface AttendanceCalendarCardProps {
  /** Every month of the session up to this one, newest first. */
  months: MonthOption[];
  selectedMonthKey: string;
  onMonthChange: (monthKey: string) => void;
  calendarDays: ParentAttendanceCalendarDay[];
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * What a day means, for the reader who hovers it.
 *
 * `NOT_MARKED` says so plainly rather than staying silent: a working day with
 * no mark is the school's register not having been taken, and a parent who sees
 * a blank square deserves to be told that instead of guessing at an absence.
 */
const STATUS_TITLE: Record<ParentAttendanceCalendarDay['status'], string> = {
  PRESENT: 'Present',
  ABSENT: 'Absent',
  LATE: 'Late',
  LEAVE: 'On leave',
  HOLIDAY: 'Holiday',
  WEEK_OFF: 'School closed',
  NOT_MARKED: 'Register not taken',
  UPCOMING: 'Still to come',
};

/** One kind of dot under a day, and what the legend calls it. */
type DotKind = 'PRESENT' | 'ABSENT' | 'LATE' | 'LEAVE' | 'CLOSED' | 'NOT_MARKED';

/**
 * Which dot a day wears. A holiday and a week off share one — both are the
 * school being shut — but a register nobody took is drawn differently, because
 * it is a working day and must not read as a day off. A day still to come has
 * nothing to show yet, so it gets no dot and no legend entry.
 */
const DOT_OF: Record<ParentAttendanceCalendarDay['status'], DotKind | null> = {
  PRESENT: 'PRESENT',
  ABSENT: 'ABSENT',
  LATE: 'LATE',
  LEAVE: 'LEAVE',
  HOLIDAY: 'CLOSED',
  WEEK_OFF: 'CLOSED',
  NOT_MARKED: 'NOT_MARKED',
  UPCOMING: null,
};

/** In the legend's order. The class is the dot, the label is its legend entry. */
const DOTS: { kind: DotKind; className: string; label: string }[] = [
  { kind: 'PRESENT', className: 'bg-emerald-500', label: 'Present' },
  { kind: 'ABSENT', className: 'bg-rose-500', label: 'Absent' },
  { kind: 'LATE', className: 'bg-amber-500', label: 'Late' },
  { kind: 'LEAVE', className: 'bg-indigo-500', label: 'Leave' },
  { kind: 'CLOSED', className: 'bg-slate-400 dark:bg-slate-500', label: 'Holiday / week off' },
  {
    kind: 'NOT_MARKED',
    className: 'border border-slate-400 dark:border-slate-500',
    label: 'Not marked',
  },
];

export function AttendanceCalendarCard({
  months,
  selectedMonthKey,
  onMonthChange,
  calendarDays,
}: AttendanceCalendarCardProps) {
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);

  const currentMonthIndex = months.findIndex(
    (m) => m.key === selectedMonthKey,
  );
  const currentMonth =
    currentMonthIndex >= 0
      ? months[currentMonthIndex]
      : months[0];

  const handlePrevMonth = () => {
    if (currentMonthIndex < months.length - 1) {
      onMonthChange(months[currentMonthIndex + 1].key);
    }
  };

  const handleNextMonth = () => {
    if (currentMonthIndex > 0) {
      onMonthChange(months[currentMonthIndex - 1].key);
    }
  };

  /**
   * Back to the current month.
   *
   * `months` is newest first and capped at today by the server, so its first
   * entry is the month we are in — no date is worked out here, because the
   * school's day is the server's to decide, not the browser's.
   */
  const handleTodayClick = () => {
    const latest = months[0];
    if (latest) onMonthChange(latest.key);
  };
  const isShowingToday = months[0]?.key === selectedMonthKey;

  /** Only the kinds of dot this month actually draws, so the legend names nothing absent. */
  const drawn = new Set(
    calendarDays.flatMap((day) => {
      const kind = day.isCurrentMonth ? DOT_OF[day.status] : null;
      return kind ? [kind] : [];
    }),
  );
  const legend = DOTS.filter((dot) => drawn.has(dot.kind));

  return (
    <div className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5 flex flex-col justify-between shadow-xs">
      {/* Card Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
        <h2 className="text-sm sm:text-base font-bold text-slate-800 dark:text-white">
          Attendance Calendar
        </h2>

        {/* Navigation Controls */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          {/* Previous Month */}
          <button
            type="button"
            onClick={handlePrevMonth}
            disabled={currentMonthIndex >= months.length - 1}
            aria-label="Previous month"
            className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {/* Month Selector Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsMonthDropdownOpen((prev) => !prev)}
              className="flex h-8 items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2.5 sm:px-3 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
            >
              <span className="text-sm">{currentMonth.label}</span>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>

            {isMonthDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-20"
                  onClick={() => setIsMonthDropdownOpen(false)}
                />
                <div className="absolute left-0 mt-1 w-44 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 shadow-xl z-30">
                  {months.map((option: MonthOption) => {
                    const isSelected = option.key === selectedMonthKey;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => {
                          onMonthChange(option.key);
                          setIsMonthDropdownOpen(false);
                        }}
                        className={`flex w-full items-center justify-between px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                          isSelected
                            ? 'bg-indigo-600 text-white font-bold'
                            : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        <span className="text-sm">{option.label}</span>
                        {isSelected && <span>✓</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Next Month */}
          <button
            type="button"
            onClick={handleNextMonth}
            disabled={currentMonthIndex <= 0}
            aria-label="Next month"
            className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          {/* Today Button */}
          {/* Filled while the month on screen is the one we are in, so the
              reader can see they are already looking at today. */}
          <button
            type="button"
            onClick={handleTodayClick}
            aria-pressed={isShowingToday}
            className={`h-8 rounded-xl border px-2.5 sm:px-3 text-sm font-bold transition-colors cursor-pointer ${
              isShowingToday
                ? 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700'
                : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
          >
            Today
          </button>
        </div>
      </div>

      {/* Weekday Labels Grid */}
      <div className="grid grid-cols-7 gap-1 pt-3 pb-2 text-center">
        {WEEKDAYS.map((day) => (
          <div key={day} className="text-xs font-bold text-slate-700 dark:text-slate-300">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days Grid */}
      <div className="grid grid-cols-7 gap-y-2.5 gap-x-1 text-center my-1">
        {/* Read-only: a day carries its status in its dot and its title, and
            there is nothing more about one day to open. */}
        {calendarDays.map((item, idx) => {
          const isCurrentMonth = item.isCurrentMonth;
          const dot = isCurrentMonth ? DOTS.find((one) => one.kind === DOT_OF[item.status]) : undefined;

          return (
            <div
              key={`${item.date}-${idx}`}
              title={item.holidayName ?? STATUS_TITLE[item.status]}
              className={`flex flex-col items-center justify-center p-1 rounded-xl ${
                !isCurrentMonth ? 'opacity-35' : ''
              }`}
            >
              {/* Day Number */}
              <div
                className={`flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full text-xs sm:text-sm font-semibold ${
                  item.isToday && isCurrentMonth
                    ? 'bg-indigo-600 text-white font-bold shadow-md shadow-indigo-500/20'
                    : isCurrentMonth
                      ? 'text-slate-800 dark:text-slate-200'
                      : 'text-slate-400 dark:text-slate-600'
                }`}
              >
                {item.dayNumber}
              </div>

              {/* Status Dot */}
              <div className="h-2 flex items-center justify-center mt-0.5">
                {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot.className}`} />}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Legend */}
      {legend.length > 0 && (
        <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-center sm:justify-start gap-4 sm:gap-6 text-xs font-semibold text-slate-600 dark:text-slate-400">
          {legend.map((dot) => (
            <div key={dot.kind} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${dot.className}`} />
              <span>{dot.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
